import "cleave.js/dist/addons/cleave-phone.be.js";
import * as React from "react";
import { QueryClient, QueryClientProvider } from "react-query";
import { HashRouter } from "react-router-dom";
import { RecoilRoot } from "recoil";
import { FullPageLoader } from "./components";
import {
  AuthProvider,
  BreadcrumbProvider,
  DialogProvider,
  EventsProvider,
  GlobalDataProvider,
  NavigationProvider,
  useDialog,
} from "./contexts";
import { useTl } from "./hooks";
import { ETLCodes } from "./locales";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // query options
      refetchOnWindowFocus: false,
    },
    mutations: {
      // mutation options
    },
  },
});

export interface AppWithRouterProvider {
  autoLoginAzureAd: boolean;
}

const AppWithRouterProvider: React.FunctionComponent<AppWithRouterProvider> = ({
  children,
  autoLoginAzureAd,
}) => {
  const { showDialog } = useDialog();
  const { t } = useTl();
  const getUserConfirmation = React.useCallback(
    (message, cb) => {
      if (message === "__SKIP__") {
        cb(true);
        return;
      }
      showDialog({
        message,
        title: t(ETLCodes.RouteConfirmDialogTitle),
        onDialogClosed: (res) => {
          if (res === "yes" || res === "ok") {
            cb(true);
          } else {
            cb(false);
          }
        },
      });
    },
    [showDialog, t]
  );

  return (
    <HashRouter getUserConfirmation={getUserConfirmation}>
      <EventsProvider>
        <AuthProvider autoLoginAzureAd={autoLoginAzureAd}>
          <GlobalDataProvider>
            <NavigationProvider>
              <BreadcrumbProvider>{children}</BreadcrumbProvider>
            </NavigationProvider>
          </GlobalDataProvider>
        </AuthProvider>
      </EventsProvider>
    </HashRouter>
  );
};

export interface IAppProviderProps {
  autoLoginAzureAd: boolean;
}

export const AppProvider: React.FunctionComponent<IAppProviderProps> = ({
  children,
  autoLoginAzureAd,
}) => {
  return (
    <React.Suspense fallback={<FullPageLoader />}>
      <RecoilRoot>
        <QueryClientProvider client={queryClient}>
          <DialogProvider>
            <AppWithRouterProvider autoLoginAzureAd={autoLoginAzureAd}>
              {children}
            </AppWithRouterProvider>
          </DialogProvider>
        </QueryClientProvider>
      </RecoilRoot>
    </React.Suspense>
  );
};
